// Cross-app light/dark continuity.
//
// The platform's three modules (Validate/Build/Sell) live on separate origins
// (venture./gtm.signalmax.ai, product-brain.com) that do NOT share localStorage, so a
// theme chosen in one app is invisible to the next. When a user hops between apps we
// carry the current theme as a `sm_theme` query param, and each app applies it on entry.
//
// next-themes stores the choice in localStorage['theme'] and reads it in a blocking
// pre-paint script, so writing that key BEFORE next-themes boots makes the destination
// open in the same mode with no flash. That's exactly what SM_THEME_BOOTSTRAP does.
//
// The param is untrusted (it rides in from another origin) so it is allowlisted to
// exactly light|dark|system before use — it only ever becomes a localStorage value and a
// theme class, never touches a redirect target.
//
// SEMANTICS OF `?sm_theme` (deliberate, do not widen): it is a TEMPORARY first-paint /
// navigation override only. The bootstrap writes it to next-themes' localStorage so the
// destination paints in the carried mode on frame one; it NEVER writes the `sm_appearance`
// cookie and NEVER writes the server row. For an authenticated user it is therefore reconciled
// AWAY by the canonical `identity_users.appearance` once AppearanceSync runs — a URL parameter
// can never silently overwrite a user's canonical appearance. (A canonical change only ever
// happens through persistAppearanceMode, i.e. an explicit in-app toggle.) For a signed-out user
// there is no canonical value, so the carried mode simply persists locally, which is the point of
// cross-app continuity.
//
// This is the FIRST-PAINT precedence (`?sm_theme` > cookie > localStorage > default). It is a
// different thing from the CANONICAL persistence precedence (`identity_users.appearance` wins once
// known); see web/components/platform/appearance.tsx for the reconciliation that enforces the latter.

export const SM_THEME_PARAM = "sm_theme"

// Per-device mirror of the user's PERSISTED appearance mode (Phase 3). Written client-side
// (non-httpOnly, so the pre-paint bootstrap can read it) whenever the user picks a mode, and
// seeded from the canonical identity_users.appearance on a device that has no cookie yet. It is
// a CACHE of the durable server preference, not the source of truth. Per-origin by nature; the
// cross-origin carry stays the `sm_theme` param, and the cross-device truth is the server value.
export const SM_APPEARANCE_COOKIE = "sm_appearance"
export const SM_APPEARANCE_MAX_AGE = 60 * 60 * 24 * 365 // 1 year

// Per-device mirror of the user's PERSISTED curated accent id (Phase 4). Same role as
// sm_appearance but for the accent: a non-httpOnly CACHE the Careers layout reads server-side to
// stamp data-accent for a flash-free first paint. The cross-device truth is identity_users
// .appearance.accent. It stores the RAW chosen id (product-agnostic); which product actually applies
// it is decided at render time by the product policy (web/lib/platform/accents.ts resolveAccent).
export const SM_ACCENT_COOKIE = "sm_accent"

const ALLOWED = ["light", "dark", "system"] as const
export type SmTheme = (typeof ALLOWED)[number]

export function isSmTheme(v: unknown): v is SmTheme {
  return typeof v === "string" && (ALLOWED as readonly string[]).includes(v)
}

/** CANONICAL persistence precedence, as a pure decision so it can be tested without a browser.
 *
 *  Given the server's stored appearance mode and whether the user has made an explicit choice in
 *  THIS session, decide whether AppearanceSync should reconcile the device to the server value.
 *  The canonical server preference WINS over a (possibly stale) device cache — that is the whole
 *  point — EXCEPT:
 *   - `serverMode` is not a valid stored mode (signed-out / anonymous / never chosen): keep the
 *     device's first-paint value; persist nothing.
 *   - the user already toggled this session: that choice is newer and was itself written to the
 *     server, so a now-stale read must not clobber it. */
export function shouldApplyServerAppearance(
  serverMode: unknown,
  sessionChoiceMade: boolean,
): { apply: false } | { apply: true; mode: SmTheme } {
  if (!isSmTheme(serverMode)) return { apply: false }
  if (sessionChoiceMade) return { apply: false }
  return { apply: true, mode: serverMode }
}

/** Client-only: mirror the chosen mode into the per-device cookie the bootstrap reads. */
export function writeAppearanceCookie(theme: string | null | undefined): void {
  if (typeof document === "undefined") return
  try {
    if (isSmTheme(theme)) {
      document.cookie = `${SM_APPEARANCE_COOKIE}=${theme}; path=/; max-age=${SM_APPEARANCE_MAX_AGE}; samesite=lax`
    } else {
      document.cookie = `${SM_APPEARANCE_COOKIE}=; path=/; max-age=0; samesite=lax`
    }
  } catch { /* blocked cookies: fall back to localStorage-only persistence */ }
}

/** Client-only: mirror the chosen curated accent id into the per-device cookie the Careers layout
 *  reads to stamp data-accent. A falsy/empty value clears it (= default orange). Callers pass a
 *  validated id (see accents.ts); even so, the SSR layout re-validates via resolveAccent, so a stray
 *  cookie can never force a non-curated accent. */
export function writeAccentCookie(accentId: string | null | undefined): void {
  if (typeof document === "undefined") return
  try {
    if (typeof accentId === "string" && accentId) {
      document.cookie = `${SM_ACCENT_COOKIE}=${accentId}; path=/; max-age=${SM_APPEARANCE_MAX_AGE}; samesite=lax`
    } else {
      document.cookie = `${SM_ACCENT_COOKIE}=; path=/; max-age=0; samesite=lax`
    }
  } catch { /* blocked cookies: fall back to server-only persistence */ }
}

/** Append the current theme to a cross-app URL. No-op when the theme is unknown or the
 *  URL already carries the param (idempotent). Works on absolute URLs and paths alike. */
export function withSmTheme(url: string, theme: string | null | undefined): string {
  if (!isSmTheme(theme)) return url
  if (new RegExp(`[?&]${SM_THEME_PARAM}=`).test(url)) return url
  return `${url}${url.includes("?") ? "&" : "?"}${SM_THEME_PARAM}=${theme}`
}

/** Blocking pre-paint bootstrap. Inlined as a <script> ahead of next-themes in the root
 *  layout so the resolved mode lands in localStorage['theme'] BEFORE next-themes applies the
 *  theme class — the page paints in the right mode on the first frame (no light<->dark flash).
 *
 *  Precedence at init (documented): `?sm_theme` param (an explicit cross-app carry, most recent
 *  action) > the `sm_appearance` cookie (this device's mirror of the user's persisted server
 *  preference) > whatever next-themes already has in localStorage (its own default/system). The
 *  server value (identity_users.appearance) is the cross-device source of truth; it seeds the
 *  cookie on write and, on a cookie-less device, via AppearanceSync after mount. */
export const SM_THEME_BOOTSTRAP =
  `(function(){try{var p=new URLSearchParams(location.search).get(${JSON.stringify(SM_THEME_PARAM)});` +
  `if(p==='light'||p==='dark'||p==='system'){localStorage.setItem('theme',p);return;}` +
  `var m=(document.cookie.match(/(?:^|; )${SM_APPEARANCE_COOKIE}=([^;]*)/)||[])[1];` +
  `if(m==='light'||m==='dark'||m==='system'){localStorage.setItem('theme',m);}}catch(e){}})();`
